import { forwardRef, useRef, useState, useEffect } from 'react';
import { generateContent, stopContentGeneration } from '../utils/contentApiUtils';
import { convertMarkdownToHTML } from '../utils/utils';
import { convertHtmlCtasToLinks } from '../utils/ctaLinkUtils';
import { processImageSuggestions } from '../features/content/utils/imageSuggestionUtils';
import './StreamedOutputReadonly.css';
import 'th-editor/dist/style.css';
import '../styles/rteStyles.css';
import { ERROR_FOCUS_MODE, ERROR_FOCUS_MODE_MESSAGE } from '../constants';

const StreamedOutputReadonly = forwardRef((
  {
    initialHTML,
    onGenerationComplete = null,
    onGenerationError = null,
    onStreaming = () => {},
    onStatus = () => {},
    generateContentFn = null,
  },
  ref
) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const[content, setContent] = useState(initialHTML);
  const generationIdRef = useRef(null);
  const readerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const accumulatedDataRef = useRef('');
  const isGeneratingRef = useRef(false);

  const decoderRef = useRef(new TextDecoder('utf-8'));
  const wholeTextRef = useRef('');

  useEffect(() => {
    setContent(initialHTML);
  }, [initialHTML]);

  const processStreamedContent = async () => {
    if (!isStreaming || !readerRef.current) {
      return;
    }
    try {
      const { done, value } = await readerRef.current.read();

      if (done) {
        console.log('Stream finished by server (reader done)');
        setIsInitializing(false);
        
        if (accumulatedDataRef.current.trim()) {
          try {
            const data = JSON.parse(accumulatedDataRef.current.trim());
            if (data.done) {
              console.log('Found done message in remaining data');
              const finalHtml = convertMarkdownToHTML(wholeTextRef.current);
              setContent(finalHtml);
              setIsStreaming(false);
              onStreaming(false);
              onStatus(null);
              isGeneratingRef.current = false;
              
              if (readerRef.current) {
                try {
                  readerRef.current.releaseLock();
                } catch (e) {
                  console.warn('Error releasing reader lock:', e);
                }
                readerRef.current = null;
              }
              
              if (onGenerationComplete) onGenerationComplete(finalHtml);
              return;
            }
          } catch (e) {
            console.warn('Error parsing remaining data:', e);
          }
        }
        
        if (wholeTextRef.current.includes(ERROR_FOCUS_MODE)) {
          setIsStreaming(false);
          onStreaming(false);
              onStatus(null);
          isGeneratingRef.current = false;
          const error = new Error(ERROR_FOCUS_MODE_MESSAGE);
          error.isUserError = true;
          if (onGenerationError) {
            onGenerationError(error);
          }
          return;
        }
        
        setIsStreaming(false);
        onStreaming(false);
              onStatus(null);
        isGeneratingRef.current = false;
        const finalHtml = convertMarkdownToHTML(wholeTextRef.current);

        setContent(finalHtml);
        
        if (readerRef.current) {
          try {
            readerRef.current.releaseLock();
          } catch (e) {
            console.warn('Error releasing reader lock:', e);
          }
          readerRef.current = null;
        }
        
        if (onGenerationComplete) onGenerationComplete(finalHtml);
        return;
      }

      const decodedChunk = decoderRef.current.decode(value);
      accumulatedDataRef.current += decodedChunk;

      let lastNewlineIndex;
      const prefixLength = 0;
      while ((lastNewlineIndex = accumulatedDataRef.current.indexOf('\n\n')) !== -1) {
        const eventString = accumulatedDataRef.current.substring(prefixLength, lastNewlineIndex).trim();
        accumulatedDataRef.current = accumulatedDataRef.current.substring(lastNewlineIndex + 2);

        if (eventString.trim()) {
          try {
            const data = JSON.parse(eventString);

            if (data.heartbeat) {
              continue;
            }

            if (data.error) {
              console.error('Generation failed - received error event', data);
              setIsInitializing(false);
              setIsStreaming(false);
              onStreaming(false);
              onStatus(null);
              isGeneratingRef.current = false;
              setContent(convertMarkdownToHTML(wholeTextRef.current));

              if (readerRef.current) {
                try {
                  readerRef.current.releaseLock();
                } catch (e) {
                  console.warn('Error releasing reader lock:', e);
                }
                readerRef.current = null;
              }

              const streamError = new Error(data.error);
              streamError.isUserError = !!data.isForUser;
              streamError.errorCode = data.errorCode;
              if (onGenerationError) {
                onGenerationError(streamError);
              }
              return;
            }

            if (data.status) {
              onStatus(data.status);
              continue;
            }

            if (isInitializing) {
              setIsInitializing(false);
              onStreaming(true);
              onStatus(null);
            }

            if (!!data.id && !generationIdRef.current) {
              generationIdRef.current = data.id;
              console.log('Updated the generation id');
            }
            if (data.id === generationIdRef.current) {
              if (data.reset) wholeTextRef.current = '';
              const markdownChunk = data.text ?? '';
              
              if (markdownChunk.includes(ERROR_FOCUS_MODE)) {
                const error = new Error(ERROR_FOCUS_MODE_MESSAGE);
                error.isUserError = true;
                throw error;
              }
              
              wholeTextRef.current += markdownChunk;
              const currentHtml = convertMarkdownToHTML(wholeTextRef.current);

              setContent(currentHtml);
            }
            if (data.done && data.id === generationIdRef.current) {
              console.log('Generation complete - received done message', { id: data.id, aborted: data.aborted });
              
              if (wholeTextRef.current.includes(ERROR_FOCUS_MODE)) {
                const error = new Error(ERROR_FOCUS_MODE_MESSAGE);
                error.isUserError = true;
                throw error;
              }
              
              const finalHtml = convertMarkdownToHTML(wholeTextRef.current);
              setContent(finalHtml);
              setIsStreaming(false);
              onStreaming(false);
              onStatus(null);
              isGeneratingRef.current = false;
              
              if (readerRef.current) {
                try {
                  readerRef.current.releaseLock();
                } catch (e) {
                  console.warn('Error releasing reader lock:', e);
                }
                readerRef.current = null;
              }
              
              if (onGenerationComplete) onGenerationComplete(finalHtml);
              return;
            }
            
            if (data.done && !generationIdRef.current) {
              console.log('Generation complete - received done message without ID');
              const finalHtml = convertMarkdownToHTML(wholeTextRef.current);
              setContent(finalHtml);
              setIsStreaming(false);
              onStreaming(false);
              onStatus(null);
              isGeneratingRef.current = false;
              
              if (readerRef.current) {
                try {
                  readerRef.current.releaseLock();
                } catch (e) {
                  console.warn('Error releasing reader lock:', e);
                }
                readerRef.current = null;
              }
              
              if (onGenerationComplete) onGenerationComplete(finalHtml);
              return;
            }
          } catch (e) {
            console.error('Error parsing event (pump):', e, eventString);
          }
        }
      }

      if (isStreaming) {
        await processStreamedContent();
      }
    } catch (error) {
      console.error('Error during stream (pump):', error);
      const partialHtml = convertMarkdownToHTML(wholeTextRef.current);
      setContent(partialHtml);
      setIsInitializing(false);
      setIsStreaming(false);
      onStreaming(false);
              onStatus(null);
      isGeneratingRef.current = false;
      if (onGenerationError) {
        onGenerationError(error);
      } else if (onGenerationComplete) {
        onGenerationComplete(partialHtml);
      }
      
      if (readerRef.current) {
        readerRef.current.releaseLock();
        readerRef.current = null;
      }
    }
  };

  const stopStreaming = async () => {
    const currentHtml = convertMarkdownToHTML(wholeTextRef.current);
    setContent(currentHtml);

    setIsInitializing(false);
    setIsStreaming(false);
    onStreaming(false);
              onStatus(null);

    if (readerRef.current) {
      try {
        await readerRef.current.cancel();
        readerRef.current.releaseLock();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Error cancelling reader during stopStreaming:', error);
        }
      } finally {
        readerRef.current = null;
      }
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (generationIdRef.current) {
      try {
        await stopContentGeneration(generationIdRef.current);
      } catch (error) {
        console.error('Error stopping backend generation:', error);
      } finally {
        generationIdRef.current = null;
      }
    }

    isGeneratingRef.current = false;

    if (onGenerationComplete) {
      console.log('stopStreaming calling onGenerationComplete');
      onGenerationComplete(currentHtml);
    }
  };

  if (ref) {
    ref.current = {
      stopStreaming,
      generateContent: async (input) => {
        if (isGeneratingRef.current) {
          console.warn("Generation already in progress.");
          return;
        }
        isGeneratingRef.current = true;
        generationIdRef.current = '';
        accumulatedDataRef.current = '';
        wholeTextRef.current = '';

        setContent('');
        setIsInitializing(true);
        onStatus(null);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
          const generator = generateContentFn || generateContent;
          const response = await generator(input, abortController.signal);

          if (abortController.signal.aborted) {
            isGeneratingRef.current = false;
            return;
          }

          if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            let isUserError = false;
            let errorCode;
            
            try {
              const errorData = await response.text();
              if (errorData) {
                try {
                  const parsedError = JSON.parse(errorData);
                  if (parsedError.isForUser && parsedError.message) {
                    errorMessage = parsedError.message;
                    isUserError = true;
                    errorCode = parsedError.errorCode;
                  } else {
                    errorMessage = parsedError.message || parsedError.error || errorMessage;
                  }
                } catch {
                  errorMessage = errorData || errorMessage;
                }
              }
            } catch {
              // Fallback to status message if can't read body
            }
            
            const error = new Error(errorMessage);
            error.isUserError = isUserError;
            error.errorCode = errorCode;
            throw error;
          }

          readerRef.current = response.body.getReader();
          setIsStreaming(true);
        } catch (error) {
          if (error.name === 'AbortError') {
            console.log('Generation request aborted by user');
            isGeneratingRef.current = false;
            return;
          }
          console.error('Error during generation request:', error);
          const partialHtml = convertMarkdownToHTML(wholeTextRef.current);
          setContent(partialHtml);
          setIsInitializing(false);
          setIsStreaming(false);
          onStreaming(false);
              onStatus(null);
          isGeneratingRef.current = false;
          if (onGenerationError) {
            onGenerationError(error);
          } else if (onGenerationComplete) {
            onGenerationComplete(partialHtml);
          }

          if (readerRef.current) {
            readerRef.current.releaseLock();
            readerRef.current = null;
          }
        }
      },
    };
  }

  useEffect(() => {
    if (isStreaming && readerRef.current) {
      processStreamedContent()
    }

    return () => {
      if (readerRef.current && isStreaming) {
        console.log("Component unmounting or stream stopped externally - cancelling reader (useEffect).");
        readerRef.current.cancel();
        readerRef.current.releaseLock();
        readerRef.current = null;
        setIsStreaming(false);
        onStreaming(false);
              onStatus(null);
        isGeneratingRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming]);

  return (
    <div className="generating-content-display" style={{ position: 'relative', minHeight: '200px' }}>
      <div className="content-div ProseMirror" dangerouslySetInnerHTML={{ __html: convertHtmlCtasToLinks(processImageSuggestions(content)) }} />
      {isInitializing && <button className='stop initializing' onClick={stopStreaming}>
          <span className="generating-spinner" />
          Generating…
        </button>}
    </div>
  );
});

export default StreamedOutputReadonly;